import React, { useState, useEffect, useRef } from 'react';
import { Search, Printer, TrendingUp, TrendingDown, ShoppingBag, DollarSign, CreditCard, Wallet } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { reportApi } from '../api';

const PAYMENT_LABELS = {
  cash:   { zh: '现金',    en: 'Cash',      km: 'សាច់ប្រាក់' },
  card:   { zh: '刷卡',    en: 'Card',      km: 'កាត' },
  khqr:   { zh: 'KHQR',   en: 'KHQR',     km: 'KHQR' },
  alipay: { zh: '支付宝',  en: 'Alipay',   km: 'Alipay' },
  mobile: { zh: '手机支付', en: 'Mobile Pay', km: 'Mobile Pay' },
  usdt:   { zh: 'USDT',   en: 'USDT',     km: 'USDT' },
  cod:    { zh: '货到付款', en: 'COD',      km: 'COD' },
};

const getPaymentLabel = (method, lang) => {
  const l = lang === 'en' ? 'en' : lang === 'km' ? 'km' : 'zh';
  return PAYMENT_LABELS[method]?.[l] || method;
};

const CHANNEL_TABS = [
  { key: 'all', label: '全部渠道' },
  { key: 'pos', label: '仅POS' },
  { key: 'h5',  label: '仅H5线上' },
];
const CHANNEL_BADGE = {
  pos: { label: 'POS', className: 'bg-blue-50 text-blue-600' },
  h5:  { label: 'H5',  className: 'bg-teal-50 text-teal-600' },
};

const today = new Date().toISOString().split('T')[0];

const getYesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

const getMonday = () => {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
};

const getFirstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

const getFirstOfLastMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().split('T')[0];
};

const getLastOfLastMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 0).toISOString().split('T')[0];
};

const SalesReportPage = () => {
  const { language, currentUser } = useApp();
  const isHQ = currentUser?.store_type === 'headquarters';
  const printRef = useRef();

  const [activeQuick, setActiveQuick] = useState('month');
  const [channel, setChannel] = useState('all');
  const [filters, setFilters] = useState({
    start_date:     getFirstOfMonth(),
    end_date:       today,
    payment_method: 'all',
    user_id:        'all',
    store_id:       'all',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const quickDates = [
    { key: 'yesterday', label: language === 'en' ? 'Yesterday'  : '昨日', start: getYesterday(),        end: getYesterday() },
    { key: 'today',     label: language === 'en' ? 'Today'      : '今日', start: today,                 end: today },
    { key: 'week',      label: language === 'en' ? 'This Week'  : '本周', start: getMonday(),           end: today },
    { key: 'month',     label: language === 'en' ? 'This Month' : '本月', start: getFirstOfMonth(),     end: today },
    { key: 'lastmonth', label: language === 'en' ? 'Last Month' : '上月', start: getFirstOfLastMonth(), end: getLastOfLastMonth() },
  ];

  useEffect(() => { loadReport(); }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = { ...filters, channel };
      if (params.payment_method === 'all') delete params.payment_method;
      if (params.user_id === 'all') delete params.user_id;
      if (params.store_id === 'all') delete params.store_id;
      const res = await reportApi.getSalesReport(params);
      if (res.success) setData(res.data);
    } catch (err) {
      console.error('加载报表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const storeName = currentUser?.store_name || '';
    const payLabel = filters.payment_method !== 'all'
      ? ` · ${getPaymentLabel(filters.payment_method, 'zh')}` : '';
    const isProfit = parseFloat(data?.summary?.total_profit || 0) >= 0;

    const summaryHtml = `
      <div style="display:flex;gap:12px;margin-bottom:16px;flex-wrap:wrap;">
        <div style="border:1px solid #e5e7eb;border-radius:8px;padding:12px;flex:1;min-width:120px;">
          <div style="color:#6b7280;font-size:11px;">销售总额</div>
          <div style="font-size:20px;font-weight:bold;color:#16a34a;margin-top:4px;">
            $${parseFloat(data?.summary?.total_sales || 0).toFixed(2)}
          </div>
        </div>
        <div style="border:1px solid #e5e7eb;border-radius:8px;padding:12px;flex:1;min-width:120px;">
          <div style="color:#6b7280;font-size:11px;">毛利润</div>
          <div style="font-size:20px;font-weight:bold;color:${isProfit ? '#16a34a' : '#dc2626'};margin-top:4px;">
            $${parseFloat(data?.summary?.total_profit || 0).toFixed(2)}
          </div>
        </div>
        <div style="border:1px solid #e5e7eb;border-radius:8px;padding:12px;flex:1;min-width:120px;">
          <div style="color:#6b7280;font-size:11px;">订单数</div>
          <div style="font-size:20px;font-weight:bold;margin-top:4px;">${data?.summary?.total_orders || 0}</div>
        </div>
        <div style="border:1px solid #e5e7eb;border-radius:8px;padding:12px;flex:1;min-width:120px;">
          <div style="color:#6b7280;font-size:11px;">平均客单价</div>
          <div style="font-size:20px;font-weight:bold;margin-top:4px;">
            $${parseFloat(data?.summary?.avg_order_value || 0).toFixed(2)}
          </div>
        </div>
      </div>
    `;

    const paymentHtml = data?.by_payment?.length > 0 ? `
      <div style="margin-bottom:16px;">
        <div style="font-weight:bold;font-size:13px;margin-bottom:8px;">支付方式汇总</div>
        ${data.by_payment.map(p => `
          <div style="display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid #f3f4f6;">
            <span>${getPaymentLabel(p.payment_method, 'zh')}</span>
            <span style="color:#16a34a;font-weight:500;">
              $${parseFloat(p.total).toFixed(2)} (${p.count}笔)
            </span>
          </div>
        `).join('')}
      </div>
    ` : '';

    const tableRows = data?.details?.map(sale => `
      <tr>
        <td>${new Date(sale.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
        <td>${sale.order_no}</td>
        <td>${sale.channel === 'h5' ? 'H5' : 'POS'}</td>
        ${isHQ ? `<td>${sale.store_name || ''}</td>` : ''}
        <td>${sale.cashier_name || ''}</td>
        <td>${sale.items_summary || ''}</td>
        <td>${getPaymentLabel(sale.payment_method, 'zh')}</td>
        <td style="text-align:right;color:#16a34a;font-weight:500;">
          $${parseFloat(sale.total_amount).toFixed(2)}
        </td>
      </tr>
    `).join('') || '';

    const win = window.open('', '_blank');
    win.document.write(`
      <html><head>
        <title>销售报表</title>
        <meta charset="utf-8">
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; color: #111; }
          h1 { font-size: 18px; margin: 0 0 4px; }
          .sub { color: #6b7280; font-size: 11px; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th { background: #f3f4f6; padding: 7px 8px; text-align: left; border: 1px solid #e5e7eb; font-size: 11px; }
          td { padding: 7px 8px; border: 1px solid #e5e7eb; font-size: 11px; }
          tfoot td { background: #f9fafb; font-weight: bold; }
          @media print { @page { margin: 15mm; } }
        </style>
      </head><body>
        <h1>销售报表</h1>
        <div class="sub">${storeName} · ${filters.start_date} ~ ${filters.end_date}${payLabel} · ${
          channel === 'all' ? '全部渠道' : channel === 'pos' ? '仅POS' : '仅H5线上'
        }</div>
        ${summaryHtml}
        ${paymentHtml}
        <div style="font-weight:bold;font-size:13px;margin-bottom:8px;">
          销售明细（${data?.details?.length || 0}条）
        </div>
        <table>
          <thead>
            <tr>
              <th>时间</th><th>订单号</th><th>渠道</th>
              ${isHQ ? '<th>门店</th>' : ''}
              <th>收银员</th><th>商品</th><th>支付</th>
              <th style="text-align:right">金额</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
          <tfoot>
            <tr>
              <td colspan="${isHQ ? 7 : 6}" style="text-align:right">合计</td>
              <td style="text-align:right;color:#16a34a;">
                $${parseFloat(data?.summary?.total_sales || 0).toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </body></html>
    `);
    win.document.close();
    setTimeout(() => win.print(), 300);
  };

  const isProfit = parseFloat(data?.summary?.total_profit || 0) >= 0;

  return (
    <div className="p-6 space-y-4">
      {/* 头部 */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">
            {language === 'en' ? 'Sales Report' : '销售报表'}
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            {language === 'en' ? 'Query and print sales data' : '查询并打印销售数据'}
          </p>
        </div>
        <button onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
          <Printer className="w-4 h-4" />
          {language === 'en' ? 'Print Report' : '打印报表'}
        </button>
      </div>

      {/* 筛选区 */}
      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        {/* 渠道切换 */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {CHANNEL_TABS.map(tab => (
            <button key={tab.key} onClick={() => setChannel(tab.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                channel === tab.key ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* 快捷日期 + 查询按钮 */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2 flex-wrap">
            {quickDates.map(q => (
              <button key={q.key}
                onClick={() => {
                  setActiveQuick(q.key);
                  setFilters(f => ({ ...f, start_date: q.start, end_date: q.end }));
                }}
                className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                  activeQuick === q.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                }`}>
                {q.label}
              </button>
            ))}
          </div>
          <button onClick={loadReport} disabled={loading}
            className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex-shrink-0">
            <Search className="w-4 h-4" />
            {loading ? '查询中...' : language === 'en' ? 'Search' : '查询'}
          </button>
        </div>

        {/* 筛选条件 */}
        <div className="flex gap-3 flex-wrap">
          <div className="flex-1 min-w-32">
            <label className="block text-xs text-gray-500 mb-1">
              {language === 'en' ? 'Start Date' : '开始日期'}
            </label>
            <input type="date" value={filters.start_date}
              onChange={e => {
                setActiveQuick(null);
                setFilters(f => ({ ...f, start_date: e.target.value }));
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex-1 min-w-32">
            <label className="block text-xs text-gray-500 mb-1">
              {language === 'en' ? 'End Date' : '结束日期'}
            </label>
            <input type="date" value={filters.end_date}
              onChange={e => {
                setActiveQuick(null);
                setFilters(f => ({ ...f, end_date: e.target.value }));
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex-1 min-w-32">
            <label className="block text-xs text-gray-500 mb-1">
              {language === 'en' ? 'Payment Method' : '支付方式'}
            </label>
            <select value={filters.payment_method}
              onChange={e => setFilters(f => ({ ...f, payment_method: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              <option value="all">{language === 'en' ? 'All' : '全部'}</option>
              <option value="cash">{language === 'en' ? 'Cash' : '现金'}</option>
              <option value="card">{language === 'en' ? 'Card' : '刷卡'}</option>
              <option value="khqr">KHQR</option>
              <option value="alipay">{language === 'en' ? 'Alipay' : '支付宝'}</option>
              <option value="usdt">USDT</option>
              <option value="cod">{language === 'en' ? 'COD' : '货到付款'}</option>
            </select>
          </div>
          <div className="flex-1 min-w-32">
            <label className="block text-xs text-gray-500 mb-1">
              {language === 'en' ? 'Cashier' : '收银员'}
              <span className="text-gray-400 font-normal ml-1">（{language === 'en' ? 'POS only' : '仅POS'}）</span>
            </label>
            <select value={filters.user_id}
              onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              <option value="all">{language === 'en' ? 'All' : '全部'}</option>
              {data?.cashiers?.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          {isHQ && (
            <div className="flex-1 min-w-32">
              <label className="block text-xs text-gray-500 mb-1">
                {language === 'en' ? 'Store' : '门店'}
              </label>
              <select value={filters.store_id}
                onChange={e => setFilters(f => ({ ...f, store_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="all">{language === 'en' ? 'All Stores' : '全部门店'}</option>
                {data?.stores?.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        {filters.user_id !== 'all' && (
          <p className="text-xs text-amber-600">⚠️ 已选择具体收银员，本次查询只显示POS数据（H5线上订单没有"收银员"这个概念）</p>
        )}
      </div>

      {/* 数据区 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : data ? (
        <div className="space-y-4">
          {/* 汇总卡片 */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: language === 'en' ? 'Total Sales'  : '销售总额',   value: `$${parseFloat(data.summary?.total_sales || 0).toFixed(2)}`,      icon: DollarSign, color: 'green'  },
              { label: language === 'en' ? 'Gross Profit' : '毛利润',     value: `$${parseFloat(data.summary?.total_profit || 0).toFixed(2)}`,      icon: isProfit ? TrendingUp : TrendingDown, color: isProfit ? 'emerald' : 'red' },
              { label: language === 'en' ? 'Orders'       : '订单数',     value: data.summary?.total_orders || 0,                                   icon: ShoppingBag, color: 'blue'  },
              { label: language === 'en' ? 'Avg. Order'   : '平均客单价', value: `$${parseFloat(data.summary?.avg_order_value || 0).toFixed(2)}`,   icon: TrendingUp,  color: 'purple'},
              { label: language === 'en' ? 'Payment Types': '支付方式数', value: data.by_payment?.length || 0,                                      icon: CreditCard,  color: 'orange'},
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="bg-white rounded-xl shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="text-2xl font-bold mt-1">{value}</p>
                  </div>
                  <div className={`bg-${color}-100 p-2 rounded-full`}>
                    <Icon className={`w-6 h-6 text-${color}-600`} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* H5配送分成收入 */}
          {(channel === 'all' || channel === 'h5') && data.h5_commission_income != null && (
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-100 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-teal-100 p-2 rounded-full">
                  <Wallet className="w-4 h-4 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {isHQ ? 'H5订单总店抽成收入' : 'H5配送分成收入'}
                  </p>
                  <p className="text-xs text-gray-500">
                    本次查询时间范围内 · {isHQ ? '总店实际抽成部分' : '本店实际分到的部分'}
                  </p>
                </div>
              </div>
              <p className="text-xl font-bold text-teal-600">${data.h5_commission_income.toFixed(2)}</p>
            </div>
          )}

          {/* 提示：H5渠道损益是全公司口径 */}
          {!isHQ && (channel === 'all' || channel === 'h5') && (
            <p className="text-xs text-gray-400">
              ℹ️ 上方"毛利润"中的H5线上部分为全公司口径，本店实际从H5订单获得的收入见上方"H5配送分成收入"
            </p>
          )}

          {/* 支付方式汇总 + 按日统计 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl shadow p-4">
              <h3 className="font-bold text-sm mb-3 flex items-center gap-1">
                <CreditCard className="w-4 h-4 text-blue-600" />
                {language === 'en' ? 'By Payment Method' : '支付方式汇总'}
                <span className="text-xs font-normal text-gray-400 ml-1">（仅POS）</span>
              </h3>
              {data.by_payment?.length > 0 ? (
                <div className="space-y-1">
                  {data.by_payment.map(p => (
                    <div key={p.payment_method} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <span className="text-sm text-gray-600">{getPaymentLabel(p.payment_method, language)}</span>
                      <div className="text-right">
                        <span className="text-sm font-medium text-green-600">${parseFloat(p.total).toFixed(2)}</span>
                        <span className="text-xs text-gray-400 ml-2">({p.count}笔)</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-4">暂无数据</p>
              )}
            </div>

            <div className="bg-white rounded-xl shadow p-4">
              <h3 className="font-bold text-sm mb-3 flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                {language === 'en' ? 'By Date' : '按日统计'}
                <span className="text-xs font-normal text-gray-400 ml-1">（仅POS）</span>
              </h3>
              {data.by_date?.length > 0 ? (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {data.by_date.map(d => (
                    <div key={d.date} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <span className="text-sm text-gray-600">
                        {typeof d.date === 'string' ? d.date : new Date(d.date).toISOString().split('T')[0]}
                      </span>
                      <div className="text-right">
                        <span className="text-sm font-medium text-green-600">${parseFloat(d.sales).toFixed(2)}</span>
                        <span className="text-xs text-gray-400 ml-2">({d.orders}笔)</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-4">暂无数据</p>
              )}
            </div>
          </div>

          {/* 销售明细 */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h3 className="font-bold">
                {language === 'en' ? 'Sales Detail' : '销售明细'}
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({data.details?.length || 0}{language === 'en' ? ' records' : ' 条'})
                </span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {language === 'en' ? 'Date' : '时间'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {language === 'en' ? 'Order No.' : '订单号'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">渠道</th>
                    {isHQ && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {language === 'en' ? 'Store' : '门店'}
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {language === 'en' ? 'Cashier' : '收银员'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {language === 'en' ? 'Items' : '商品'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {language === 'en' ? 'Payment' : '支付'}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      {language === 'en' ? 'Amount' : '金额'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.details?.length > 0 ? data.details.map(sale => {
                    const badge = CHANNEL_BADGE[sale.channel] || CHANNEL_BADGE.pos;
                    return (
                      <tr key={`${sale.channel}-${sale.id}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {new Date(sale.created_at).toLocaleString('zh-CN', {
                            month: '2-digit', day: '2-digit',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-800">
                          {sale.order_no}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
                            {badge.label}
                          </span>
                        </td>
                        {isHQ && (
                          <td className="px-4 py-3 text-sm text-gray-600">{sale.store_name || '—'}</td>
                        )}
                        <td className="px-4 py-3 text-sm text-gray-600">{sale.cashier_name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                          {sale.items_summary}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                            {getPaymentLabel(sale.payment_method, language)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600 text-right whitespace-nowrap">
                          ${parseFloat(sale.total_amount).toFixed(2)}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={isHQ ? 8 : 7} className="px-4 py-12 text-center text-gray-400 text-sm">
                        {language === 'en' ? 'No data' : '暂无数据'}
                      </td>
                    </tr>
                  )}
                </tbody>
                {data.details?.length > 0 && (
                  <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                    <tr>
                      <td colSpan={isHQ ? 7 : 6} className="px-4 py-3 text-sm font-bold text-gray-700 text-right">
                        {language === 'en' ? 'Total' : '合计'}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-green-600 text-right">
                        ${parseFloat(data.summary?.total_sales || 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SalesReportPage;